import { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  donutChartConfigSelector,
  donutChartConfigDispatcher,
} from "@store/slices/donut-chart-config-slice";
import { useGetDatasetQuestionsResponseMutation } from "@store/api/dataset-api";
import { getChartColor } from "@utils/chart-utils";
import { resolveVisualizationBaseFilters } from "@utils/visualization-base-search";
import { mergeBaseFiltersWithExistingFilters } from "@utils/visualization-base-utils";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import type { VisualizationBase } from "@models/VisualizationBase";

type WeightSelection = "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";

interface UseDonutChartDataReturn {
  fetchChartData: (
    weight: WeightSelection,
    shouldApplyFilters: boolean,
    overrideDates?: { startDate: string | null; endDate: string | null },
    overrideStatTestingEnabled?: boolean,
    overridePeriodCount?: number | null,
    overrideVisualizationBase?: VisualizationBase | null,
  ) => Promise<void>;
  isRefetchingData: boolean;
  weightsApplied: boolean;
  setWeightsApplied: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useDonutChartData(): UseDonutChartDataReturn {
  const dispatch = useAppDispatch();
  const {
    currentConfig,
    datasetId,
    questionId,
    activeFilters,
    question: questions,
  } = useAppSelector(donutChartConfigSelector);

  const [weightsApplied, setWeightsApplied] = useState<boolean>(true);

  const [getDatasetQuestionsResponse, { isLoading: isRefetchingData }] =
    useGetDatasetQuestionsResponseMutation();

  const { updateChartData, updateColorPalette, clearRollingAverageResponseFields } =
    donutChartConfigDispatcher(dispatch);

  const rollingAverageRef = useRef(currentConfig.rollingAverage);
  rollingAverageRef.current = currentConfig.rollingAverage;

  const fetchChartData = useCallback(
    async (
      weight: WeightSelection,
      shouldApplyFilters: boolean,
      overrideDates?: { startDate: string | null; endDate: string | null },
      overrideStatTestingEnabled?: boolean,
      overridePeriodCount?: number | null,
      overrideVisualizationBase?: VisualizationBase | null,
    ) => {
      if (!datasetId || !questionId) return;

      try {
        let startDate: string | null | undefined =
          overrideDates !== undefined
            ? overrideDates.startDate
            : currentConfig.timePeriodSelection?.startDate;
        let endDate: string | null | undefined =
          overrideDates !== undefined
            ? overrideDates.endDate
            : currentConfig.timePeriodSelection?.endDate;

        const explicitNoDates =
          overrideDates !== undefined &&
          !overrideDates.startDate &&
          !overrideDates.endDate;
        const reduxIsAllTime =
          currentConfig.timePeriodSelection?.timePeriod === "all_time";
        const skipDates =
          explicitNoDates || (overrideDates === undefined && reduxIsAllTime);

        if (!skipDates && !startDate && !endDate) {
          if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
            startDate = currentConfig.responsesMinDate;
            endDate = currentConfig.responsesMaxDate;
          }
        }

        const shouldIncludeDates = !skipDates && !!(startDate && endDate);
        const statTestingEnabled =
          overrideStatTestingEnabled !== undefined
            ? overrideStatTestingEnabled
            : currentConfig.statisticalTesting?.enabled;

        clearRollingAverageResponseFields();

        const effectiveVisualizationBase =
          overrideVisualizationBase !== undefined
            ? overrideVisualizationBase
            : currentConfig.visualizationBase;
        const baseFilters = await resolveVisualizationBaseFilters(
          dispatch,
          datasetId,
          effectiveVisualizationBase,
        );
        const dashboardFilters =
          shouldApplyFilters && activeFilters.length > 0 ? activeFilters : [];
        const combinedFilters = mergeBaseFiltersWithExistingFilters(
          dashboardFilters,
          baseFilters,
        );

        const result = await getDatasetQuestionsResponse({
          id: questionId,
          datasetId: datasetId,
          topN: null,
          sorting: "ORIGINAL",
          weightSelection: weight,
          chartType: "DONUT",
          filters: combinedFilters.length > 0 ? combinedFilters : undefined,
          ...(currentConfig.focusRollup
            ? {
                focusRollup: currentConfig.focusRollup,
                ...(currentConfig.focusMode && {
                  focusMode: currentConfig.focusMode,
                }),
              }
            : currentConfig.focusMode && currentConfig.focusEntity
              ? {
                  focusMode: currentConfig.focusMode,
                  focusEntity: currentConfig.focusEntity,
                }
              : {}),
          ...(shouldIncludeDates && {
            startDate: startDate!,
            endDate: endDate!,
          }),
          ...(statTestingEnabled &&
            shouldIncludeDates && {
              enableStatTesting: true,
              statTestingType: testTypeToApiFormat(
                currentConfig.statisticalTesting?.testType,
              ),
              pValue: significanceLevelToPValue(
                currentConfig.statisticalTesting?.significanceLevel || "90%",
              ),
              minimumSampleSize:
                currentConfig.statisticalTesting?.minimumSampleSize || 30,
            }),
          ...(() => {
            if (skipDates) return {};
            const ra = rollingAverageRef.current;
            const pc =
              overridePeriodCount !== undefined
                ? overridePeriodCount
                : ra?.enabled
                  ? ra.windowSize
                  : null;
            return pc ? { periodCount: pc } : {};
          })(),
        });

        if (result.data?.data && Array.isArray(result.data.data)) {
          const processedData = result.data.data.map(
            (item: any, index: number) => {
              // Extract value from new descriptiveMetrics structure with fallback to percentage
              let value = 0;
              let valueFound = false;
              const firstSurvey = item.surveys?.[0];

              if (firstSurvey) {
                // Try descriptiveMetrics first (new structure)
                if (
                  firstSurvey.descriptiveMetrics &&
                  firstSurvey.descriptiveMetrics.length > 0
                ) {
                  const metricValue = firstSurvey.descriptiveMetrics[0].value;
                  if (metricValue !== undefined && metricValue !== null) {
                    value = metricValue;
                    valueFound = true;
                  }
                }
                // Fallback to percentage field (old structure) only if no value was found
                if (
                  !valueFound &&
                  firstSurvey.percentage !== undefined &&
                  firstSurvey.percentage !== null
                ) {
                  value = firstSurvey.percentage;
                }
              }

              const baseN =
                firstSurvey?.descriptiveMetrics?.[0]?.baseN ?? null;
              const cellN =
                firstSurvey?.descriptiveMetrics?.[0]?.cellN ?? null;
              // Stat testing writes the -1/0/1 marker into descriptiveMetrics
              // (same place Bar Chart reads it from) — the legacy top-level
              // significantDifference field is only a fallback for older
              // response shapes that predate descriptiveMetrics.
              const significantDifference =
                firstSurvey?.descriptiveMetrics?.[0]?.statSignificance ??
                firstSurvey?.significantDifference ??
                0;

              return {
                name: item.variableLabel,
                y: value,
                color: getChartColor(index),
                baseN,
                cellN,
                significantDifference,
              };
            },
          );

          // Chart N (PRD-41): unweighted respondent count for the whole
          // chart — the maximum slice baseN, falling back to responseCount.
          const baseNValues = processedData
            .map((item) => item.baseN)
            .filter((n): n is number => typeof n === "number");
          const chartN: number | null =
            baseNValues.length > 0
              ? Math.max(...baseNValues)
              : (result.data?.question?.responseCount ?? null);

          updateChartData({
            categories: result.data.data.map((item: any) => item.variableLabel),
            series: [
              {
                name: questions?.name || "Data",
                data: processedData,
              },
            ],
            responsesMinDate: result.data?.responsesMinDate,
            responsesMaxDate: result.data?.responsesMaxDate,
            chartN,
            effectiveRollingPeriodCount: result.data?.effectiveRollingPeriodCount,
            rollingPeriodsWarningMessage: result.data?.rollingPeriodsWarningMessage,
            comparisonRollingLabel: result.data?.comparisonRollingLabel,
            missingRollingPeriodLabels: result.data?.missingRollingPeriodLabels,
          });

          // Re-apply the current color palette to the new data
          updateColorPalette(currentConfig.colorPalette);

          // Update weightsApplied state from API response
          if (result.data?.weightsApplied !== undefined) {
            setWeightsApplied(result.data.weightsApplied);
          }
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    },
    [
      dispatch,
      datasetId,
      questionId,
      questions?.name,
      activeFilters,
      getDatasetQuestionsResponse,
      updateChartData,
      updateColorPalette,
      clearRollingAverageResponseFields,
      currentConfig.colorPalette,
      currentConfig.visualizationBase,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.statisticalTesting,
      currentConfig.focusMode,
      currentConfig.focusEntity,
      currentConfig.focusRollup,
    ],
  );

  return { fetchChartData, isRefetchingData, weightsApplied, setWeightsApplied };
}
